import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import api from '../../api';

// Initial State
const initialState = {
  generalReport: [],
  trackingMetrics: [],
  trackingSummary: {},
  homeVisitReport: [],
  opdReport: [],
  opdConsultants: [],
  opdConversions: {},
  isLoading: false,
  error: null,
};

/* ==============================
   ASYNC THUNKS
 ============================== */

export const fetchGeneralReport = createAsyncThunk(
  'reports/fetchGeneralReport',
  async (filters = {}, { rejectWithValue }) => {
    try {
      const response = await api.get('/reports/general', { params: filters });
      return response.data.data;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.error || 'Failed to fetch general report'
      );
    }
  }
);

export const fetchTrackingMetrics = createAsyncThunk(
  'reports/fetchTrackingMetrics',
  async (filters = {}, { rejectWithValue }) => {
    try {
      const response = await api.get('/reports/tracking-metrics', { params: filters });
      return response.data; // Includes 'data' (metrics table) and 'summary' (cards)
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.error || 'Failed to fetch tracking metrics'
      );
    }
  }
);

export const fetchHomeVisitReport = createAsyncThunk(
  'reports/fetchHomeVisitReport',
  async (filters = {}, { rejectWithValue }) => {
    try {
      const response = await api.get('/reports/home-visits', { params: filters });
      return response.data;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.error || 'Failed to fetch home visit report'
      );
    }
  }
);

export const fetchOPDReport = createAsyncThunk(
  'reports/fetchOPDReport',
  async (filters = {}, { rejectWithValue }) => {
    try {
      const response = await api.get('/reports/opd', { params: filters });
      return response.data; // Includes 'data' (summary) and 'consultants' (breakdown)
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.error || 'Failed to fetch OPD report'
      );
    }
  }
);

/* ==============================
   SLICE
 ============================== */

const reportSlice = createSlice({
  name: 'reports',
  initialState,
  reducers: {
    clearReportsError: (state) => {
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    builder
      // General Report
      .addCase(fetchGeneralReport.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchGeneralReport.fulfilled, (state, action) => {
        state.isLoading = false;
        state.generalReport = action.payload;
      })
      .addCase(fetchGeneralReport.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      })

      // Tracking Metrics
      .addCase(fetchTrackingMetrics.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchTrackingMetrics.fulfilled, (state, action) => {
        state.isLoading = false;
        state.trackingMetrics = action.payload.data;
        state.trackingSummary = action.payload.summary;
      })
      .addCase(fetchTrackingMetrics.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      })

      // Home Visit Report
      .addCase(fetchHomeVisitReport.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchHomeVisitReport.fulfilled, (state, action) => {
        state.isLoading = false;
        state.homeVisitReport = action.payload.data;
        state.homeVisitYTD = action.payload.ytd_total;
      })
      .addCase(fetchHomeVisitReport.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      })

      // OPD Report
      .addCase(fetchOPDReport.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchOPDReport.fulfilled, (state, action) => {
        state.isLoading = false;
        state.opdReport = action.payload.data;
        state.opdConsultants = action.payload.consultants;
        state.opdConversions = action.payload.conversions;
      })
      .addCase(fetchOPDReport.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      });
  },
});

export const { clearReportsError } = reportSlice.actions;

export default reportSlice.reducer;
